/** @format */

import { ParentGroupSummary } from "@question-bank/shared";
import { AppQuestionCard } from "./AppQuestionCard";
import "./QuestionList.css";

interface QuestionListProps {
	questions: ParentGroupSummary[];
	total: number;
	limit: number;
	offset: number;
	isLoading?: boolean;
	onPageChange: (offset: number) => void;
	onQuestionClick?: (question: ParentGroupSummary) => void;
}

const SkeletonCard = () => (
	<div className="question-card skeleton-card" aria-hidden>
		<div className="skeleton-line skeleton-title" />
		<div className="skeleton-line skeleton-body" />
		<div className="skeleton-line skeleton-body short" />
		<div className="skeleton-footer">
			<div className="skeleton-line skeleton-badge" />
			<div className="skeleton-line skeleton-badge" />
		</div>
	</div>
);

export const QuestionList = ({
	questions,
	total,
	limit,
	offset,
	isLoading = false,
	onPageChange,
	onQuestionClick,
}: QuestionListProps) => {
	const currentPage = Math.floor(offset / limit) + 1;
	const totalPages = Math.ceil(total / limit);

	const handlePrevious = () => {
		if (offset > 0) {
			onPageChange(Math.max(0, offset - limit));
		}
	};

	const handleNext = () => {
		if (offset + limit < total) {
			onPageChange(offset + limit);
		}
	};

	if (isLoading && questions.length === 0) {
		return (
			<div className="question-list">
				<div className="results-header">
					<span className="results-count">Loading…</span>
				</div>
				<div className="questions-grid">
					{Array.from({ length: limit }, (_, i) => (
						<SkeletonCard key={i} />
					))}
				</div>
			</div>
		);
	}

	if (questions.length === 0) {
		return (
			<div className="no-results">
				<p>No questions found matching your criteria.</p>
				<p>Try adjusting your filters.</p>
			</div>
		);
	}

	return (
		<div className={`question-list ${isLoading ? "question-list--loading" : ""}`}>
			<div className="results-header">
				<span className="results-count">
					Showing {(offset + 1).toLocaleString()}-{Math.min(offset + limit, total).toLocaleString()} of{" "}
					{total.toLocaleString()} questions
				</span>
			</div>

			<div className="questions-grid">
				{questions.map((question) => (
					<AppQuestionCard
						key={question.parentQuestionId}
						question={question}
						onClick={() => onQuestionClick?.(question)}
					/>
				))}
			</div>

			{totalPages > 1 && (
				<div className="pagination">
					<button className="page-button" onClick={handlePrevious} disabled={offset === 0}>
						← Previous
					</button>
					<span className="page-info">
						Page {currentPage} of {totalPages}
					</span>
					<button className="page-button" onClick={handleNext} disabled={offset + limit >= total}>
						Next →
					</button>
				</div>
			)}
		</div>
	);
};
